/**
 * MilestoneCelebration - Confetti celebration modal with share
 * Triggers on achievements like first country, rank promotion, etc.
 */
import { useState, useEffect } from "react";
import { 
  Trophy, Globe, Flame, Star, Share2, X, 
  Sparkles, PartyPopper 
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { shareViaWebAPI } from "@/hooks/useNativeShare";
import axios from "axios";
import { API } from "@/App";
import confetti from "canvas-confetti";

const iconMap = {
  trophy: Trophy,
  globe: Globe,
  flame: Flame,
  star: Star,
  map: Globe,
  award: Trophy,
};

const MilestoneCelebration = ({ onDismiss }) => {
  const [celebration, setCelebration] = useState(null);
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    checkForCelebrations();
  }, []);
  
  const checkForCelebrations = async () => {
    try {
      const response = await axios.get(`${API}/premium/celebrations/check`);
      if (response.data.has_celebration) {
        setCelebration(response.data.celebration);
        setVisible(true);
        triggerConfetti();
      }
    } catch (error) {
      console.error("Failed to check celebrations:", error);
    }
  };
  
  const triggerConfetti = () => {
    // Fire confetti from both sides
    const defaults = {
      spread: 360,
      ticks: 100,
      gravity: 0.8,
      decay: 0.94,
      startVelocity: 30,
      shapes: ["star", "circle"],
      colors: ["#FF6B6B", "#FFE66D", "#4ECDC4", "#45B7D1", "#96CEB4"]
    };
    
    // Left burst
    confetti({
      ...defaults,
      particleCount: 50,
      origin: { x: 0.2, y: 0.6 }
    });
    
    // Right burst
    confetti({
      ...defaults,
      particleCount: 50,
      origin: { x: 0.8, y: 0.6 }
    });
    
    // Center burst
    setTimeout(() => {
      confetti({
        ...defaults,
        particleCount: 100,
        origin: { x: 0.5, y: 0.5 }
      });
    }, 250);
  };
  
  const handleShare = async () => {
    if (!celebration?.share_text) return;
    
    const shared = await shareViaWebAPI(
      celebration.share_text,
      window.location.origin,
      celebration.title
    );
    if (shared) {
      toast.success("Shared successfully!");
    } else {
      try {
        await navigator.clipboard.writeText(celebration.share_text);
        toast.success("Copied to clipboard!");
      } catch (error) {
        // silent
      }
    }
  };
  
  const handleDismiss = async () => {
    if (celebration?.id) {
      try {
        await axios.post(`${API}/premium/celebrations/${celebration.id}/dismiss`);
      } catch (error) {
        console.error("Failed to dismiss celebration:", error);
      }
    }
    setVisible(false);
    setCelebration(null);
    onDismiss?.();
  };
  
  if (!visible || !celebration) return null;
  
  const IconComponent = iconMap[celebration.icon] || Trophy;
  
  return (
    <Dialog open={visible} onOpenChange={(open) => !open && handleDismiss()}>
      <DialogContent className="bg-gradient-to-br from-charcoal via-charcoal to-charcoal/95 border-coral/30 sm:max-w-md text-center">
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-4 text-white/60 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Celebration Icon */}
        <div className="pt-4">
          <div className="relative inline-block">
            <div className="absolute inset-0 animate-ping bg-coral/30 rounded-full" />
            <div className="relative p-6 bg-gradient-to-br from-coral to-coral/80 rounded-full">
              <IconComponent className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>
        
        {/* Sparkles decoration */}
        <div className="absolute top-20 left-8">
          <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
        </div>
        <div className="absolute top-16 right-12">
          <PartyPopper className="w-5 h-5 text-coral animate-bounce" />
        </div>
        
        {/* Title */}
        <DialogHeader className="pt-4">
          <DialogTitle className="text-2xl font-bold text-white text-center">
            {celebration.title}
          </DialogTitle>
        </DialogHeader>
        
        {/* Description */}
        <p className="text-white/70 text-center px-4">
          {celebration.description}
        </p>
        
        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4">
          {celebration.share_text && (
            <Button
              onClick={handleShare}
              className="bg-coral hover:bg-coral/90 text-white"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Achievement
            </Button>
          )}
          
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="text-white/60 hover:text-white"
          >
            Continue Exploring
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MilestoneCelebration;
